import { useEffect, useRef, useState } from 'react';
import {
  Grid2,
  Button,
  Stack,
  Typography,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import RequiredIndicator from 'components/@extended/RequiredIndicator';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import { createQualificationPack, updateQualificationPack } from '../../services/qualificationPack.service';
import { getAllSkillSectors } from '../../services/skillSector.service';
import { getAllNOS } from '../../services/nos.service';

const STORAGE_KEY = 'qualification_pack_draft';

interface QualificationPackFormValues {
  sscId: string;
  qpCode: string;
  qpName: string;
  nsqfLevel: number | string;
  version: string;
  totalMarks: number | string;
  passPercentage: number | string;
  status: 'Active' | 'Inactive';
  nosIds: string[];
  isLocked?: boolean;
}

const AddQualificationPack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useAuth();

  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.data || null;

  const [sscs, setSscs] = useState<any[]>([]);
  const [loadingSscs, setLoadingSscs] = useState(false);
  const isMounted = useRef(false);
  const [sscPage, setSscPage] = useState(1);
  const [sscLimit] = useState(10);
  const [sscHasMore, setSscHasMore] = useState(true);
  const [sscSearch, setSscSearch] = useState('');

  const [nosOptions, setNosOptions] = useState<any[]>([]);
  const [loadingNos, setLoadingNos] = useState(false);

  const fetchSscs = async (page = 1, searchValue = '') => {
    if (!sscHasMore && page !== 1) return;

    try {
      setLoadingSscs(true);

      const params = {
        search: searchValue,
        limit: sscLimit,
        skip: 1
      };

      const res = await getAllSkillSectors(params);

      if (res?.status === 200) {
        const newData = res?.data?.data?.skillSectors || res?.data?.data || [];

        setSscs((prev) => (page === 1 ? newData : [...prev, ...newData]));

        if (newData.length < sscLimit) {
          setSscHasMore(false);
        }
      }
    } catch {
      Snackbar('Failed to load Sector Skill Councils', 'error');
    } finally {
      setLoadingSscs(false);
    }
  };

  useEffect(() => {
    setSscPage(1);
    setSscHasMore(true);
    fetchSscs(1, sscSearch);
  }, [organization?._id, sscSearch]);

  const fetchNOS = async () => {
    if (!organization?._id) return;

    try {
      setLoadingNos(true);
      const params = {
        limit: 1000,
        skip: 1
      };

      const res = await getAllNOS(params);

      if (res?.status === 200) {
        const list = res?.data?.result?.NOS || res?.data?.data || [];
        setNosOptions(list);
      }
    } catch {
      Snackbar('Failed to load National Occupational Standards', 'error');
    } finally {
      setLoadingNos(false);
    }
  };

  useEffect(() => {
    fetchNOS();
  }, [organization?._id]);

  const normalizeNosIds = (raw: any): string[] => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => (typeof item === 'string' ? item : item?._id))
      .filter((id) => typeof id === 'string' && id);
  };

  // ================= INITIAL VALUES =================
  const getInitialValues = (): QualificationPackFormValues => {
    if (isEdit && editData) {
      return {
        sscId: editData?.sscId?._id || editData?.sscId || '',
        qpCode: editData?.qpCode || '',
        qpName: editData?.qpName || '',
        nsqfLevel: editData?.nsqfLevel || '',
        version: editData?.version || '1.0',
        totalMarks: editData?.totalMarks || '',
        passPercentage: editData?.passPercentage || '',
        status: editData?.status || 'Active',
        nosIds: normalizeNosIds(editData?.nosIds),
        isLocked: editData?.isLocked ?? false
      };
    }

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const draft = JSON.parse(stored);

        const isExpired = Date.now() - draft.timestamp > 24 * 60 * 60 * 1000;

        if (!isExpired && draft?.organizationId === organization?._id) {
          return {
            ...draft.values,
            nosIds: normalizeNosIds(draft.values?.nosIds)
          };
        }
      } catch {
        console.warn('Invalid draft found');
      }
    }

    return {
      sscId: '',
      qpCode: '',
      qpName: '',
      nsqfLevel: '',
      version: '1.0',
      totalMarks: '',
      passPercentage: '',
      status: 'Active',
      nosIds: [],
      isLocked: false
    };
  };

  const initialValues = getInitialValues();

  // ================= VALIDATION =================
  const validationSchema = Yup.object({
    sscId: Yup.string().required('Sector Skill Council is required'),
    qpCode: Yup.string().trim().min(3).max(20).required('QP Code is required'),
    qpName: Yup.string().trim().min(3).max(150).required('QP Name is required'),
    nsqfLevel: Yup.number()
      .min(1, 'NSQF Level must be at least 1')
      .max(8, 'NSQF Level cannot exceed 8')
      .required('NSQF Level is required')
      .typeError('Must be a number')
      .integer('Must be a whole number'),
    version: Yup.string()
      .matches(/^\d+(\.\d+)*$/, 'Version must be like 1.0, 2.1')
      .required('Version is required'),
    // totalMarks: Yup.number().min(1).required('Total Marks is required').typeError('Must be a number'),
    passPercentage: Yup.number()
      .min(0, 'Pass percentage cannot be negative')
      .max(100, 'Pass percentage cannot exceed 100')
      .required('Pass Percentage is required')
      .typeError('Must be a number'),
    status: Yup.string().oneOf(['Active', 'Inactive']).required(),
    nosIds: Yup.array()
      .of(Yup.string().required())
      .min(1, 'At least one NOS is required')
  });

  // ================= SUBMIT =================
  const handleSubmit = async (values: QualificationPackFormValues, helpers: FormikHelpers<QualificationPackFormValues>) => {
    helpers.setSubmitting(true);

    const payload = {
      sscId: values.sscId,
      qpCode: values.qpCode.trim(),
      qpName: values.qpName.trim(),
      nsqfLevel: Number(values.nsqfLevel),
      version: values.version.trim(),
      passPercentage: Number(values.passPercentage),
      status: values.status,
      nosIds: normalizeNosIds(values.nosIds),
      ...(values.isLocked !== undefined && { isLocked: values.isLocked })
    };

    try {
      let res;
      if (isEdit && editData?._id) {
        res = await updateQualificationPack(editData._id, payload);
        Snackbar('Qualification Pack updated successfully', 'success');
      } else {
        res = await createQualificationPack(payload);
        Snackbar('Qualification Pack created successfully', 'success');
      }

      if (res?.status === 200 || res?.status === 201) {
        localStorage.removeItem(STORAGE_KEY);
        navigate(-1);
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save Qualification Pack';

      Snackbar(errorMessage, 'error');
      if (errorMessage?.includes('locked')) {
        Snackbar('This Qualification Pack is locked and cannot be modified.', 'info');
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <MainCard
          content={false}
          title={
            <Stack direction="row" spacing={2} alignItems="center">
              <BackToggle show />
              <Stack>
                <Typography variant="h5">
                  <FormattedMessage id={isEdit ? 'Edit Qualification Pack' : 'Add Qualification Pack'} />
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {isEdit && editData?.isAssignedToCourse
                    ? 'This Qualification Pack is assigned to one or more courses and cannot be modified or unlocked.'
                    : isEdit
                      ? 'Edit Qualification Pack details. You can unlock if not assigned to a course.'
                      : 'Add Qualification Pack details'}
                </Typography>
              </Stack>
            </Stack>
          }
        >
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isValid, dirty, isSubmitting }) => {
              const formDisabled = isEdit && Boolean(editData?.isAssignedToCourse);
              // ================= AUTO SAVE DRAFT =================
              useEffect(() => {
                if (isEdit) return;

                if (!isMounted.current) {
                  isMounted.current = true;
                  return;
                }

                if (dirty) {
                  const draftData = {
                    organizationId: organization?._id,
                    values
                  };

                  localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
                }
              }, [values, dirty, isEdit, organization?._id]);

              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Box p={3}>
                    <Grid2 container spacing={2.5}>
                      {/* SSC */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          Sector Skill Council <RequiredIndicator />
                        </InputLabel>
                        <Select
                          fullWidth
                          name="sscId"
                          value={values.sscId}
                          onChange={(e: SelectChangeEvent<string>) => setFieldValue('sscId', e.target.value)}
                          onBlur={handleBlur}
                          error={touched.sscId && Boolean(errors.sscId)}
                          disabled={loadingSscs || formDisabled}
                          displayEmpty
                          MenuProps={{
                            PaperProps: {
                              onScroll: (event: any) => {
                                const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;

                                if (bottom && sscHasMore && !loadingSscs) {
                                  const nextPage = sscPage + 1;
                                  setSscPage(nextPage);
                                  fetchSscs(nextPage, sscSearch);
                                }
                              }
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Sector Skill Council
                          </MenuItem>
                          {sscs.map((ssc) => (
                            <MenuItem key={ssc._id} value={ssc._id}>
                              {ssc.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText error>{touched.sscId && errors.sscId}</FormHelperText>
                      </Grid2>

                      {/* QP Code */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          QP Code <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="qpCode"
                          value={values.qpCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.qpCode && Boolean(errors.qpCode)}
                          disabled={formDisabled}
                        />
                        <FormHelperText error>{touched.qpCode && errors.qpCode}</FormHelperText>
                      </Grid2>

                      {/* QP Name */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          QP Name <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="qpName"
                          value={values.qpName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.qpName && Boolean(errors.qpName)}
                          disabled={formDisabled}
                        />
                        <FormHelperText error>{touched.qpName && errors.qpName}</FormHelperText>
                      </Grid2>

                      {/* Version */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          Version <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="version"
                          value={values.version}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.version && Boolean(errors.version)}
                          disabled={isEdit || formDisabled}
                          readOnly={isEdit}
                        />
                        <FormHelperText error>{touched.version && errors.version}</FormHelperText>
                      </Grid2>

                      {/* NSQF */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          NSQF Level <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          type="number"
                          name="nsqfLevel"
                          value={values.nsqfLevel}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.nsqfLevel && Boolean(errors.nsqfLevel)}
                          disabled={formDisabled}
                          inputProps={{
                            min: 1,
                            max: 8,
                            step: 1
                          }}
                        />
                        <FormHelperText error>{touched.nsqfLevel && errors.nsqfLevel}</FormHelperText>
                      </Grid2>

                      {/* Total Marks */}
                      {/* <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Total Marks</InputLabel>
                        <OutlinedInput
                          fullWidth
                          type="number"
                          name="totalMarks"
                          disabled={true}
                          value={values.totalMarks}
                          // onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.totalMarks && Boolean(errors.totalMarks)}
                        />
                        <FormHelperText error>{touched.totalMarks && errors.totalMarks}</FormHelperText>
                      </Grid2> */}

                      {/* Pass % */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          Pass Percentage <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          type="number"
                          name="passPercentage"
                          value={values.passPercentage}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.passPercentage && Boolean(errors.passPercentage)}
                          disabled={formDisabled}
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: 1
                          }}
                        />
                        <FormHelperText error>{touched.passPercentage && errors.passPercentage}</FormHelperText>
                      </Grid2>

                      {/* NOS Multi-select */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          NOS <RequiredIndicator />
                        </InputLabel>
                        <Select
                          fullWidth
                          multiple
                          name="nosIds"
                          value={values.nosIds || []}
                          onChange={(e) => setFieldValue('nosIds', e.target.value)}
                          onBlur={handleBlur}
                          disabled={loadingNos || nosOptions.length === 0 || formDisabled}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected || (Array.isArray(selected) && selected.length === 0)) return 'Select NOS';
                            const selectedArray = Array.isArray(selected) ? selected : [selected];
                            const labels = nosOptions
                              .filter((nos) => selectedArray.includes(nos._id))
                              .map((nos) => `${nos.nosCode} - ${nos.nosName}`);
                            return labels.join(', ');
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select NOS
                          </MenuItem>
                          {nosOptions.map((nos) => (
                            <MenuItem key={nos._id} value={nos._id}>
                              <Checkbox checked={values.nosIds.indexOf(nos._id) > -1} />
                              <ListItemText primary={`${nos.nosCode} - ${nos.nosName}`} />
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText error>{touched.nosIds && errors.nosIds}</FormHelperText>
                      </Grid2>

                      {/* Status */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          Status <RequiredIndicator />
                        </InputLabel>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.status === 'Active'}
                              onChange={(e) => setFieldValue('status', e.target.checked ? 'Active' : 'Inactive')}
                              color="primary"
                              disabled={formDisabled}
                            />
                          }
                          label={values.status === 'Active' ? 'Active' : 'Inactive'}
                        />
                      </Grid2>

                      {/* Lock: on Add optional; on Edit can unlock if not assigned to course */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Lock Qualification Pack</InputLabel>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.isLocked ?? false}
                              onChange={(e) => setFieldValue('isLocked', e.target.checked)}
                              color="warning"
                              disabled={formDisabled}
                            />
                          }
                          label={
                            values.isLocked
                              ? 'Locked (cannot be modified once assigned to a course)'
                              : 'Unlocked'
                          }
                        />
                      </Grid2>
                    </Grid2>

                    <Stack direction="row" justifyContent="flex-end" mt={4} spacing={2}>
                      <Button variant="outlined" onClick={() => navigate(-1)} disabled={isSubmitting}>
                        Cancel
                      </Button>

                      {!formDisabled && (
                        <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || (isEdit && !dirty)}>
                          {isEdit ? 'Update Qualification Pack' : 'Create Qualification Pack'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </MainCard>
      </Grid2>
    </Grid2>
  );
};

export default AddQualificationPack;